import { ReprocessAssetRequestConfig, useStorytellClient } from "@storytell/client";
import type { Asset } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { TbAlertTriangle, TbCheck, TbRefresh } from "solid-icons/tb";
import { createSignal, Match, Switch } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire/WireProvider";

type RetryStatus = "idle" | "loading" | "success" | "error";

interface AssetTryAgainButtonProps {
  asset: Asset;
  /** Visual variant. `solid` = filled brand button (asset card). `subtle` = text-only with hover wash (dense rows). */
  variant?: "solid" | "subtle";
  /** Extra classes appended to the button (e.g. tighter padding for dense rows). */
  class?: string;
}

export const AssetTryAgainButton = (props: AssetTryAgainButtonProps) => {
  const { t } = useI18n();
  const wire = useWire();
  const client = useStorytellClient();

  const [retryStatus, setRetryStatus] = createSignal<RetryStatus>("idle");
  const [retryError, setRetryError] = createSignal<string | null>(null);

  const handleRetry = async (e: MouseEvent) => {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    if (retryStatus() === "loading") return;
    trackFeature({ feature: "assets", action: "retry", via: "button" });

    const authToken = wire.auth.token();
    if (!authToken) {
      setRetryStatus("error");
      setRetryError(t("asset_try_again_error"));
      window.setTimeout(() => setRetryStatus("idle"), 4000);
      return;
    }

    setRetryStatus("loading");
    setRetryError(null);
    try {
      const request = ReprocessAssetRequestConfig.withBearerToken(authToken);
      const response = await client.fetch(request, { id: props.asset.id }, undefined);
      if (response.isErr()) {
        const msg = (response.error as { message?: string })?.message ?? t("asset_try_again_error");
        console.error("Failed to reprocess asset:", response.error);
        setRetryError(msg);
        setRetryStatus("error");
        window.setTimeout(() => setRetryStatus("idle"), 4000);
        return;
      }
      wire.assets.set(props.asset.id, response.value);
      setRetryStatus("success");
      window.setTimeout(() => setRetryStatus("idle"), 2000);
    } catch (err) {
      console.error("Failed to reprocess asset:", err);
      setRetryError(t("asset_try_again_error"));
      setRetryStatus("error");
      window.setTimeout(() => setRetryStatus("idle"), 4000);
    }
  };

  const variant = () => props.variant ?? "solid";

  const variantClass = () => {
    if (variant() === "subtle") {
      if (retryStatus() === "success") {
        return "text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40";
      }
      if (retryStatus() === "error") {
        return "text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40";
      }
      return "text-brand hover:bg-brand/10";
    }
    if (retryStatus() === "success") return "bg-emerald-600 hover:bg-emerald-700 text-white";
    if (retryStatus() === "error") return "bg-red-600 hover:bg-red-700 text-white";
    return "bg-brand hover:bg-brand-hover text-on-brand";
  };

  return (
    <StButton
      type="button"
      onClick={(e) => void handleRetry(e)}
      loading={retryStatus() === "loading"}
      icon={retryStatus() === "success" ? TbCheck : retryStatus() === "error" ? TbAlertTriangle : TbRefresh}
      noColor
      noBorder={variant() === "subtle"}
      class={twMerge("shrink-0 px-2 py-0.5 text-xs", variantClass(), props.class)}
      iconClass="!bg-transparent hover:!bg-transparent text-current"
      title={retryStatus() === "error" ? (retryError() ?? t("asset_try_again_error")) : t("asset_try_again")}
    >
      <Switch fallback={t("asset_try_again")}>
        <Match when={retryStatus() === "success"}>{t("asset_try_again_success")}</Match>
        <Match when={retryStatus() === "error"}>{t("asset_try_again_error")}</Match>
      </Switch>
    </StButton>
  );
};
